// Field Labels screens, rev 2.8. Pure: state in, HTML out.
// Mockup: MOCKUP_FIELD_LABELS_REV2.html rev 2.8 (approved 2026-10-23). Words: FIELD_LABELS_WORDS.md sections 14 to 21.
// Left: You Are In and Equipment Type, nothing else. Right: the room as grid paper, one piece of gear open at a time.
// Actions use data-fl so they never collide with the Creator's data-ai or the survey's data-sv.

import { esc } from "./training-screens";
import { DEFAULT_SIZES, fitOf, sizeKey, sizeName, sizeOfLabel, TORO_MAX_SIZES, type LabelSize, type PrintFile, type Problem } from "./field-labels";
import {
  OUTSIDE,
  breakerNames,
  isGridMain,
  circuitsOf,
  coversOf,
  gearSummary,
  isBlank,
  isTandem,
  MAX_COVERS,
  isPanel,
  isSec,
  nextLetter,
  ownerOf,
  PANEL_SIZES,
  polesTakeIn,
  roomLetters,
  roomNames,
  ROOM_PARTS,
  recodeProblem,
  GEAR_TAG_MAX,
  ROOM_DESCRIPTION_MAX,
  spaceKey,
  splitRange,
  SPLIT_LETTERS,
  type FieldSite,
  type Gear,
  type RoomKindRev2,
  type SiteLabel,
  type SiteLineup,
  type SiteRoom,
  roomLight,
} from "./field-labels-site";

/** Print preview pixels per inch of label: a 2 x 9 is 270 px long, as in the mockup. */
export const PREVIEW_PX = 30;

const plural = (n: number, one: string, many = `${one}s`): string => `${n} ${n === 1 ? one : many}`;

export interface PageView {
  siteNumber: string;
  siteName: string;
  total: number;
  /** Push To Assessment (ASSESSMENT_FLOW_B5_WORDS.md). Missing: the button reads plainly until the gate is read. */
  push?: PushView;
}

/** Where the room you are in stands against the Assessment Sheet (PUSH_BY_ROOM_B7_WORDS.md items 1 to 3 and 7). */
export interface PushView {
  /** The room You Are In. Null: no room yet. */
  room: string | null;
  /** The room is marked Done: its push is open (item 3). */
  done: boolean;
  /** No push from this room yet and none of its rows on the sheet: the first push. */
  first: boolean;
  /** New rows the room would bring, rows waiting in the run left out. Null: the sheet's rows are not on this iPad yet. */
  fresh: number | null;
  /** Rows from Field Labels the walk took out, and ones it put back: this room's and gone rooms' (items 6 and 10). */
  remove?: number;
  restore?: number;
}

/**
 * The bar button (MOCKUP_PUSH_BY_ROOM.html, approved 2026-10-29 5:05 PM). Push ER1 · 55 the first time, Update Push
 * ER1 · 1 New · 2 Remove after, ER1 On Sheet when nothing is left. Grey until ER1 is marked Done; a tap says why.
 */
export function pushButton(v: PushView | undefined): string {
  const room = v?.room ?? null;
  const btn = (words: string, off: boolean) =>
    `<button class="ac-tbtn fl-push${off ? " off" : " dark"}" type="button" data-fl="push" id="fl-push" aria-disabled="${off}">${words}</button>`;
  if (!v || !room) return btn("Push", true);
  const r = esc(room);
  const changes = [v.fresh ? `${v.fresh} New` : "", v.remove ? `${v.remove} Remove` : "", v.restore ? `${v.restore} Put Back` : ""].filter(Boolean);
  if (v.fresh === null) return btn(`Push ${r}`, !v.done);
  if (!changes.length) return btn(v.first ? `Push ${r}` : `${r} On Sheet`, true);
  if (v.first && !v.remove && !v.restore) return btn(`Push ${r} · ${v.fresh}`, !v.done);
  return btn(`Update Push ${r} · ${changes.join(" · ")}`, !v.done);
}

export function fieldLabelsPage(v: PageView): string {
  return `<div class="fl-page">
  <div class="ac-bar">
    <div class="ac-crumbs"><a href="#/assets">Assessment Sheet</a><span class="sv-sep">›</span><a href="#/assets/${esc(v.siteNumber)}">${esc(v.siteName)}</a><span class="sv-sep">›</span><b>Field Labels</b></div>
    <small class="fl-saved" id="fl-saved">Saved on this iPad</small>
    <button class="ac-tbtn" type="button" data-fl="undo" id="fl-undo" disabled>Undo</button>
    <button class="ac-tbtn" type="button" data-fl="sizes">Label Sizes</button>
    ${pushButton(v.push)}
    <button class="ac-tbtn dark" type="button" data-fl="preview" id="fl-print">Print Field Labels · ${v.total}</button>
  </div>
  <div id="fl-line"></div>
  <div class="fl-main" id="fl-main">
    <div class="fl-left" id="fl-left"></div>
    <div class="fl-right" id="fl-right"></div>
  </div>
  <div id="fl-overlay"></div>
</div>`;
}

const LIGHT_WORDS: Record<string, string> = { gray: "Not started", yellow: "Started", green: "Done" };
/** A space's light: gray nothing yet, yellow started, green done. */
export const light = (l: string): string => `<span class="fl-light ${l}" role="img" aria-label="${LIGHT_WORDS[l]}"></span>`;

/** The one line under the bar: only when something needs you. Red first. */
export function problemLine(all: readonly Problem[]): string {
  const p = all.find((x) => x.tone === "red") ?? all[0];
  if (!p) return "";
  const more = all.length - 1;
  const text = `${esc(p.text)}${more > 0 ? ` And ${more} more.` : ""}`;
  // A line on a label is a way there: a tap opens that gear and flashes the label (walk item 11).
  if (p.key) return `<button class="ac-line ${p.tone} fl-goline" type="button" data-fl="goto" data-key="${esc(p.key)}">${text}</button>`;
  return `<div class="ac-line ${p.tone}">${text}</div>`;
}

// ---- Left: where and what ----

export interface LeftView {
  room: SiteRoom | null;
  quick: readonly string[];
}

export function leftHtml(v: LeftView): string {
  const r = v.room;
  const btn = r
    ? `<b>${esc(r.name)}</b><small><span class="fl-roomwhere" data-fl-where>${roomWhere(r.description)}</span>${plural(r.gear.length, "piece")} of gear<br>Tap to change room</small>`
    : `<b>Rooms</b><small>Set up the rooms<br>from your walk</small>`;
  const dis = r ? "" : " disabled";
  return `<div><div class="fl-hd">You Are In</div><button class="fl-roombtn" type="button" data-fl="rooms">${r ? light(roomLight(r)) : ""}${btn}</button>${r ? roomPhotoBox(r) : ""}</div>
    <div><div class="fl-hd">Equipment Type</div><div class="fl-gear">${v.quick.map((t) => `<button class="fl-gbtn" type="button" data-fl="add" data-type="${esc(t)}"${dis}>${esc(t)}</button>`).join("")}<button class="fl-gbtn more" type="button" data-fl="more"${dis}>More</button></div></div>`;
}

// ---- Room and gear photos (ROOM_GEAR_PHOTO_B5_WORDS.md, MOCKUP_ROOM_GEAR_PHOTO.html approved 2026-10-29) ----

/** The camera mark, the same in the box, on a closed card and on the Rooms sheet. */
export const CAMERA = `<svg class="ph-ic" viewBox="0 0 24 24" aria-hidden="true"><path d="M4 8h3l1.6-2.2h6.8L17 8h3v11H4z" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/><circle cx="12" cy="13.3" r="3.4" fill="none" stroke="currentColor" stroke-width="1.8"/></svg>`;

/** A small picture. The page fills it from the iPad, or the cloud for a photo another iPad took (walk-photos.ts). */
const thumb = (path: string) => `<img data-thumb="${esc(path)}" alt="">`;

/** Under You Are In: Add Room Photo, or the photo small. One short line, so the equipment types stay in reach. */
export function roomPhotoBox(r: SiteRoom): string {
  return r.photo
    ? `<button class="ph-room have" type="button" data-fl="photo" data-r="${esc(r.name)}">${thumb(r.photo.path)}<span><b>Room Photo</b><small>Tap to see it full size</small></span></button>`
    : `<button class="ph-room empty" type="button" data-fl="photo" data-r="${esc(r.name)}">${CAMERA}<span>Add Room Photo</span></button>`;
}

/** On the open card, right after the gear's name (field test 2026-10-29 8:58 AM): Add Photo, or the photo small. */
export function gearPhotoBox(r: SiteRoom, g: Gear): string {
  return g.photo
    ? `<button class="ph-gear have" type="button" data-fl="photo" data-r="${esc(r.name)}" data-g="${esc(g.id)}" aria-label="${esc(g.name)} photo">${thumb(g.photo.path)}</button>`
    : `<button class="ph-gear empty" type="button" data-fl="photo" data-r="${esc(r.name)}" data-g="${esc(g.id)}">${CAMERA}<span>Add Photo</span></button>`;
}

/** Full size, with Retake and Close. The line under it says where it goes. */
export function photoSheet(title: string, path: string, driveName: string): string {
  return `<div class="ai-sheet" data-fl-sheet><div class="ai-card ph-full">
    <div class="ai-cardhd"><b>${esc(title)}</b><span class="fl-hdbtns"><button class="ai-tbtn" type="button" data-fl="photoretake">Retake</button><button class="ai-tbtn dark" type="button" data-fl="close">Close</button></span></div>
    <div class="ph-big">${thumb(path)}<span class="ph-wait" data-thumb-wait="${esc(path)}" hidden>This photo is on another iPad. It shows here with signal.</span></div>
    <div class="ph-cap">Waits on this iPad and sends to Drive on signal: 00 Field Labels / Photos / ${esc(driveName)}</div>
  </div></div>`;
}

// ---- Right: the room as grid paper ----

export interface RightView {
  room: SiteRoom | null;
  /** The gear open now. Null: every card is closed. */
  open: string | null;
  /** "ER2", or null when there is no next room (pick from the sheet). */
  next: string | null;
  /** Red outline on one square or circle, from the print preview. */
  flash?: { gearId: string; sec?: number; space?: number; circuit?: number; own?: boolean } | null;
  /** Order mode: a grab handle on each card, Cancel and Done in the heading (B3, drag and drop). */
  ordering?: boolean;
  /** Changes made since Order was tapped. Cancel puts them back. */
  orderChanges?: number;
  /** Remove tapped on this gear: the heading asks Are You Sure? (field test 2026-10-28 4:43 PM). */
  rmAsk?: string | null;
  /** Gear with an Asset ID saved on the Assessment Sheet (ASSESSMENT_FLOW_B7_PART2_WORDS.md item 1). Empty until read. */
  onSheet?: ReadonlyMap<string, readonly string[]>;
  /** On Sheet gear unlocked for a rename or remove (item 4). */
  unlocked?: ReadonlySet<string>;
  /** Unlock tapped on this gear: the heading asks Are You Sure? */
  unlockAsk?: string | null;
  /** The room's last push, as a time (item 9). Empty: not pushed. */
  pushedAt?: string;
}

/** How a card stands with the sheet: not on it, On Sheet and locked, or unlocked. */
type SheetState = "none" | "locked" | "open";
function sheetStateOf(v: RightView, g: Gear): SheetState {
  if (!v.onSheet?.has(g.id)) return "none";
  return v.unlocked?.has(g.id) ? "open" : "locked";
}

/** "Subpanel Of PNL-01", or "" for top gear. */
function parentWords(r: SiteRoom, g: Gear): string {
  const p = g.parentId ? r.gear.find((x) => x.id === g.parentId) : undefined;
  return p ? `Subpanel Of ${p.name}` : "";
}

/** The closed card's grey line: the plant's tag first, then subpanel, then the count. */
function metaLine(r: SiteRoom, g: Gear): string {
  return [g.tag ?? "", parentWords(r, g), gearSummary(r, g)].filter(Boolean).join(" · ");
}

export function rightHtml(v: RightView): string {
  const r = v.room;
  if (!r) return `<div class="fl-empty">Start with the rooms you found on the walk.<br>Tap <b>Rooms</b> on the left: ER 1 to 5, an EA, ER1 Part A and B, or Outside.</div>`;
  const letters = roomLetters(r);
  const ordering = !!v.ordering && r.gear.length > 1;
  const cards = ordering ? orderBlocks(r) : r.gear.map((g) => (g.id === v.open ? openCard(r, g, letters, v.flash ?? null, v.rmAsk === g.id, sheetStateOf(v, g), v.unlockAsk === g.id) : closedCard(r, g, false, sheetStateOf(v, g)))).join("");
  const desc = `<label class="fl-desc"><span>Room Description</span><input type="text" data-fl-in="desc" data-r="${esc(r.name)}" value="${esc(r.description ?? "")}" maxlength="${ROOM_DESCRIPTION_MAX}" autocomplete="off" autocapitalize="words" spellcheck="false"></label>`;
  const order = ordering
    ? `<div class="fl-orderbar"><span class="fl-orderhint">Hold ≡ and drag. Names follow.</span><span class="fl-hdbtns"><button class="ai-tbtn" type="button" data-fl="ordercancel">Cancel</button><button class="ai-tbtn dark" type="button" data-fl="orderdone">Done</button></span></div>${
        v.orderChanges ? `<div class="fl-chgline">${plural(v.orderChanges, "change")} since you tapped Order. Cancel puts ${v.orderChanges === 1 ? "it" : "them"} back. Done keeps ${v.orderChanges === 1 ? "it" : "them"}.</div>` : ""
      }`
    : r.gear.length > 1
      ? `<button class="fl-tog" type="button" data-fl="order">Order</button>`
      : "";
  // Item 5 and 9: Pushed time, the small Room Done beside Order. Room Done marks the room; it never moves you.
  const pushed = v.pushedAt ? `<span class="fl-pushed">Pushed ${esc(pushedWhen(v.pushedAt))}</span>` : "";
  const roomDone = r.gear.length ? `<button class="fl-tog fl-roomdone${r.done ? " on" : ""}" type="button" data-fl="roomdone" aria-pressed="${r.done ? "true" : "false"}">Room Done</button>` : "";
  const btns = ordering ? "" : `<span class="fl-rbtns">${pushed}${roomDone}${order}</span>`;
  return `<div class="fl-rhd"><b>${esc(r.name)}</b>${desc}<span>${r.gear.length ? "Hanging order, top to bottom" : ""}</span>${btns}</div>${ordering ? order : ""}${
    cards || `<div class="fl-empty">Nothing in ${esc(r.name)} yet.<br>Tap the equipment type on the left.</div>`
  }`;
}

const GRIP = `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 7h16M4 12h16M4 17h16" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" fill="none"/></svg>`;

/** Order mode: each top piece of gear is a block that drags with its subpanels; subpanels drag inside it. */
function orderBlocks(r: SiteRoom): string {
  const tops = r.gear.filter((g) => !g.parentId || !r.gear.some((x) => x.id === g.parentId));
  return tops
    .map((t) => {
      const kids = r.gear.filter((k) => k.parentId === t.id);
      return `<div class="fl-oblk" data-drag-item="${esc(t.id)}">${closedCard(r, t, true)}${kids.length ? `<div class="fl-okids">${kids.map((k) => closedCard(r, k, true)).join("")}</div>` : ""}</div>`;
    })
    .join("");
}

/** "5:12 PM" today, "Oct 28 5:12 PM" another day. */
export function pushedWhen(at: string, now = new Date()): string {
  const d = new Date(at);
  if (Number.isNaN(d.getTime())) return "";
  const time = d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  return d.toDateString() === now.toDateString() ? time : `${d.toLocaleDateString("en-US", { month: "short", day: "numeric" })} ${time}`;
}

/** Green On Sheet, or amber Unlocked, beside the name (MOCKUP_PUSH_BY_ROOM.html screen 5; Part 2 screen 3). */
function sheetPill(ss: SheetState): string {
  return ss === "locked" ? `<span class="fl-sheetpill">On Sheet</span>` : ss === "open" ? `<span class="fl-sheetpill open">Unlocked</span>` : "";
}

function closedCard(r: SiteRoom, g: Gear, order = false, ss: SheetState = "none"): string {
  const sub = `${g.parentId ? " sub" : ""}${g.shockOnly ? " shock" : ""}`;
  // In Order the card is only a handle and its name: the drag starts on the handle, the rest of the card scrolls.
  const kid = order && g.parentId ? ` data-drag-item="${esc(g.id)}"` : "";
  const handle = order ? `<button class="fl-handle" type="button" aria-label="Hold and drag ${esc(g.name)}">${GRIP}</button>` : "";
  const name = order ? `<span class="fl-lab sm">${esc(g.name)}</span>` : `<button class="fl-lab sm" type="button" data-fl="open" data-g="${esc(g.id)}">${esc(g.name)}</button>`;
  // Item 11: no green Done on a closed card. Green on a card means On Sheet now.
  const right = order ? "" : `<button class="fl-tog fl-edit" type="button" data-fl="open" data-g="${esc(g.id)}">Edit</button>`;
  return `<div class="fl-card done${sub}" data-gear="${esc(g.id)}"${kid}><div class="fl-chead">${handle}${name}<span class="fl-meta">${esc(metaLine(r, g))}</span>${g.photo ? `<span class="ph-mark" role="img" aria-label="Has a photo">${CAMERA}</span>` : ""}${g.shockOnly ? `<span class="fl-shockpill">Shock Label</span>` : ""}${sheetPill(ss)}${right}</div></div>`;
}

function openCard(r: SiteRoom, g: Gear, letters: Map<string, string[]>, flash: RightView["flash"], rmAsk = false, ss: SheetState = "none", unlockAsk = false): string {
  const own = flash && flash.gearId === g.id && flash.own ? " flash" : "";
  let body = "";
  // One-label gear says nothing more on the open card: the name says it (field test 2026-10-29 8:58 AM).
  let meta = "";
  if (isSec(g)) {
    body = sectionGrid(r, g, letters, flash && flash.gearId === g.id ? flash : null);
    meta = gearSummary(r, g);
  } else if (isPanel(g)) {
    body = panelHtml(g, flash && flash.gearId === g.id ? flash.circuit ?? null : null);
    const feeders = Object.keys(g.circuits).filter((t) => !isGridMain(g, Number(t))).length;
    meta = `${plural(feeders, "breaker")}${g.main ? " with main" : feeders ? " · MLO" : ""} · tap each one, skip the spaces`;
  }
  if (g.shockOnly) meta = "No field label";
  const pw = parentWords(r, g);
  if (pw) meta = meta ? `${pw} · ${meta}` : pw;
  const shock = `<button class="fl-tog${g.shockOnly ? " on" : ""}" type="button" data-fl="shockonly" data-g="${esc(g.id)}" aria-pressed="${g.shockOnly === true}">Shock Only</button>`;
  const tag = `<div class="fl-tagline"><label class="fl-tagrow"><span>Sites Equipment Tag</span><input type="text" data-fl-in="tag" data-g="${esc(g.id)}" value="${esc(g.tag ?? "")}" maxlength="${GEAR_TAG_MAX}" autocomplete="off" autocapitalize="characters" spellcheck="false"></label>${shock}</div>${
    g.shockOnly ? `<div class="fl-shockline">Fed over 50 V and under 208 V: a shock label goes on in place of the field label. No field label or QR label prints.</div>` : ""
  }`;
  // On Sheet: locked by default, Unlock where Remove is (ASSESSMENT_FLOW_B7_PART2_WORDS.md items 2 and 4).
  const heading =
    ss === "locked" && unlockAsk
      ? `<span class="fl-hdbtns fl-cend fl-unlockask"><span>Unlock ${esc(g.name)}? A rename or remove changes the Assessment Sheet on the next push. Are You Sure?</span><button class="fl-rmno" type="button" data-fl="unlockno">Cancel</button><button class="fl-unlock yes" type="button" data-fl="unlockyes" data-g="${esc(g.id)}">Unlock</button></span>`
      : ss === "locked"
        ? `<span class="fl-hdbtns fl-cend"><button class="fl-unlock" type="button" data-fl="unlock" data-g="${esc(g.id)}">Unlock</button><button class="fl-hdone" type="button" data-fl="done">Done</button></span>`
        : rmAsk
          ? `<span class="fl-hdbtns fl-cend fl-rmask"><span>Remove ${esc(g.name)}? Are You Sure?</span><button class="fl-rmno" type="button" data-fl="rmno">Cancel</button><button class="fl-rm yes" type="button" data-fl="rmyes" data-g="${esc(g.id)}">Remove</button></span>`
          : `<span class="fl-hdbtns fl-cend"><button class="fl-rm" type="button" data-fl="rmgear" data-g="${esc(g.id)}">Remove</button><button class="fl-hdone" type="button" data-fl="done">Done</button></span>`;
  const sheetLine =
    ss === "locked"
      ? `<div class="fl-lockline">On the Assessment Sheet. Its name, room and ${isSec(g) ? "buckets" : "place"} hold here, so each Asset ID matches the sheet.${isSec(g) ? " New buckets still add." : ""}</div>`
      : ss === "open"
        ? `<div class="fl-lockline open">Unlocked. Changes here reach the Assessment Sheet on the next push. It locks again after the push.</div>`
        : "";
  return `<div class="fl-card cur" data-gear="${esc(g.id)}"><div class="fl-chead"><button class="fl-lab${own}" type="button" data-fl="lab" data-g="${esc(g.id)}">${esc(g.name)}</button>${gearPhotoBox(r, g)}${meta ? `<span class="fl-meta">${esc(meta)}</span>` : ""}${sheetPill(ss)}${heading}</div>${sheetLine}${tag}${body}</div>`;
}

/** Columns per section, a square per space top to bottom. The last column is the next section, gray until tapped. */
export function sectionGrid(r: SiteRoom, g: Gear, letters: Map<string, string[]>, flash: { sec?: number; space?: number } | null, view?: DrawingView): string {
  const ls = letters.get(g.id) ?? [];
  const rows = Math.max(10, Math.max(0, ...g.sections) + 1);
  // The survey's view shows the buckets only: no Next section, no empty squares below them.
  const nx = view ? null : nextLetter(r, g.id);
  const cols = g.sections.map((c, i) => ({ c, letter: ls[i] ?? "?", i, isNew: false }));
  if (nx) cols.push({ c: 0, letter: nx, i: g.sections.length, isNew: true });
  const col = (c: (typeof cols)[number]) => {
    const [ctop, cbot] = c.isNew ? [0, 0] : coversOf(g, c.i);
    // Covers at the bottom close the section: no gray squares below them (PANEL_MODEL_B1_WORDS.md section 2).
    const shown = cbot || view ? c.c : rows;
    const cover = (k: number, where: string) => `<div class="fl-cover" aria-label="Cover, ${where}" data-k="${k}">Cover</div>`;
    const tops = Array.from({ length: ctop }, (_, k) => cover(k, "on top")).join("");
    const bots = Array.from({ length: cbot }, (_, k) => cover(k, "at bottom")).join("");
    const squares = Array.from({ length: shown }, (_, k) => {
      const space = k + 1;
      const lit = space <= c.c;
      const p = `${c.letter}${space}`;
      const ways = g.splits[spaceKey(c.i, space)];
      const spare = g.spares.includes(spaceKey(c.i, space));
      const fl = flash && flash.sec === c.i && flash.space === space ? " flash" : "";
      const cls = `fl-sq${lit ? " lit" : ""}${lit && ways ? " split" : ""}${lit && spare ? " spare" : ""}${fl}`;
      if (view) {
        // A split bucket's first part opens (B5a); Next Bucket steps through the rest.
        const key = ways ? `${p}a` : p;
        return `<button type="button" class="${cls}${view.picked === key || (ways && view.picked?.startsWith(p)) ? " flash" : ""} fl-vbtn" data-cd="${esc(view.act)}" data-cd-v="${esc(key)}" aria-label="${esc(p)}">${drawLight(view.light(key))}${esc(ways ? splitRange(p, ways) : p)}</button>`;
      }
      return `<button type="button" class="${cls}" data-fl="sq" data-g="${esc(g.id)}" data-s="${c.i}" data-k="${space}" aria-label="${esc(lit ? p : `${p}, empty`)}">${lit ? esc(ways ? splitRange(p, ways) : p) : space}</button>`;
    }).join("");
    const hd = view
      ? `<div class="fl-colhd">Section ${c.i + 1}<b>${esc(c.letter)}</b></div>`
      : c.isNew
      ? `<div class="fl-colhd">Next<b>${esc(c.letter)}</b></div>`
      : `<button type="button" class="fl-colhd fl-sechd" data-fl="sechd" data-g="${esc(g.id)}" data-s="${c.i}" aria-label="Section ${c.i + 1}, ${esc(c.letter)}: covers">Section ${c.i + 1}<b>${esc(c.letter)}</b></button>`;
    return `<div class="fl-col${c.isNew ? " new" : ""}">${hd}${tops}${squares}${bots}</div>`;
  };
  return `<div class="fl-cols">${cols.map(col).join("")}</div>`;
}

/**
 * The panel as it looks (mockup MOCKUP_FIELD_LABELS_PANEL_COUNT.html, approved 2026-10-23, 3:17 PM):
 * a solid bus down the middle, circuit numbers beside it (odd left, even right), breakers outside the numbers.
 * A 2 or 3 pole is one block across its rows. Tap a gray spot to add a breaker.
 */
/**
 * The drawing as the survey shows it (SURVEY_CARDS_C1_WORDS.md item 9): the same gear front, for tapping only.
 * Each breaker or bucket carries its survey light; a tap sends `act` with its key. The walk's edit buttons stay
 * on Field Labels.
 */
export interface DrawingView {
  act: string;
  /** The light for a breaker (its lowest circuit, "11") or a bucket (its position, "A2"). */
  light: (key: string) => "none" | "started" | "missing" | "done";
  picked: string | null;
}

const drawLight = (s: "none" | "started" | "missing" | "done") => `<span class="cl-dot ${s} fl-vdot" aria-hidden="true"></span>`;

export function panelHtml(g: Gear, flash: number | null, view?: DrawingView): string {
  const names = breakerNames(g);
  const rows = g.panelSize / 2;
  const r0 = 2;
  let cells = `<div class="fl-pside" style="grid-row:1;grid-column:1">Odd</div><div class="fl-pside" style="grid-row:1;grid-column:5">Even</div>`;
  cells += `<div class="fl-pbus" style="grid-row:${r0} / ${r0 + rows}"></div>`;
  for (let r = 0; r < rows; r++) cells += `<div class="fl-pnum l" style="grid-row:${r0 + r}">${2 * r + 1}</div><div class="fl-pnum r" style="grid-row:${r0 + r}">${2 * r + 2}</div>`;
  for (let c = 1; c <= g.panelSize; c++) {
    const row = r0 + Math.ceil(c / 2) - 1;
    const col = c % 2 ? 1 : 5;
    const top = ownerOf(g, c);
    if (view && top === null) {
      cells += `<div class="${isBlank(g, c) ? "fl-pblank" : "fl-pslot"} fl-vcell" style="grid-row:${row};grid-column:${col}" aria-hidden="true">${isBlank(g, c) ? "Blank" : ""}</div>`;
      continue;
    }
    if (top === null && isBlank(g, c)) {
      cells += `<button type="button" class="fl-pblank${flash === c ? " flash" : ""}" data-fl="cc" data-g="${esc(g.id)}" data-n="${c}" style="grid-row:${row};grid-column:${col}" aria-label="Circuit ${c}, blank">Blank</button>`;
      continue;
    }
    if (top === null) {
      cells += `<button type="button" class="fl-pslot" data-fl="cc" data-g="${esc(g.id)}" data-n="${c}" style="grid-row:${row};grid-column:${col}" aria-label="Circuit ${c}, space"></button>`;
      continue;
    }
    if (top !== c) continue;
    const poles = g.circuits[String(top)]!;
    const fl = flash === top ? " flash" : "";
    const name = names.get(top) ?? "";
    const tdm = isTandem(g, top);
    const main = isGridMain(g, top);
    const body = tdm ? `<span class="fl-tdm"><i>${esc(name)}A</i><i>${esc(name)}B</i></span>` : `${esc(name)}${poles > 1 ? `<small>${poles} pole</small>` : ""}`;
    if (view) {
      const key = String(top);
      cells += `<button type="button" class="fl-pbrk${poles === 1 ? " one" : ""}${tdm ? " tdm" : ""}${main ? " main" : ""}${view.picked === key ? " flash" : ""} fl-vbtn" data-cd="${esc(view.act)}" data-cd-v="${key}" style="grid-row:${row} / span ${poles};grid-column:${col}" aria-label="${esc(`${name}, circuits ${circuitsOf(top, poles)}`)}">${drawLight(view.light(key))}${body}</button>`;
      continue;
    }
    cells += `<button type="button" class="fl-pbrk${poles === 1 ? " one" : ""}${tdm ? " tdm" : ""}${main ? " main" : ""}${fl}" data-fl="cc" data-g="${esc(g.id)}" data-n="${top}" style="grid-row:${row} / span ${poles};grid-column:${col}" aria-label="${esc(`${name}, circuits ${circuitsOf(top, poles)}${tdm ? ", tandem A and B" : ""}`)}">${body}</button>`;
  }
  const count = Object.keys(g.circuits).length;
  const sizes = PANEL_SIZES.map((n) => `<button class="fl-tog sz${g.panelSize === n ? " on" : ""}" type="button" data-fl="psize" data-g="${esc(g.id)}" data-n="${n}">${n}</button>`).join("");
  // A main in the grid reads MB in its cell: no bar above or below, and no Main At choice (B3).
  const inGrid = g.main && g.mainCircuit !== undefined;
  if (view)
    return `${g.main && !inGrid && g.mainAt !== "bottom" ? `<div class="fl-pmb">MB · Main Breaker</div>` : ""}<div class="fl-can">${cells}</div>${g.main && !inGrid && g.mainAt === "bottom" ? `<div class="fl-pmb bottom">MB · Main Breaker</div>` : ""}`;
  return `<div class="fl-prow"><button class="fl-tog${g.main ? " on" : ""}" type="button" data-fl="main" data-g="${esc(g.id)}" aria-pressed="${g.main}">Main Breaker ${g.main ? "On" : "Off"}</button>${
    // Lit means it is there: Main At Bottom lights black and gold; Main At Top stays plain (field test, 2:35 PM).
    g.main && !inGrid ? `<button class="fl-tog${g.mainAt === "bottom" ? " on" : ""}" type="button" data-fl="mainat" data-g="${esc(g.id)}" aria-pressed="${g.mainAt === "bottom"}">${g.mainAt === "bottom" ? "Main At Bottom" : "Main At Top"}</button>` : ""
  }<button class="fl-tog${g.breakerLabels ? " on" : ""}" type="button" data-fl="brklabels" data-g="${esc(g.id)}" aria-pressed="${g.breakerLabels === true}">Breaker Labels ${g.breakerLabels ? "On" : "Off"}</button>
    <span class="fl-meta">Panel</span>${sizes}${count || g.main ? `<button class="fl-tog clear" type="button" data-fl="clearbk" data-g="${esc(g.id)}">Clear All</button>` : ""}</div>
    ${g.main && !inGrid && g.mainAt !== "bottom" ? `<div class="fl-pmb">MB · Main Breaker</div>` : ""}
    <div class="fl-can">${cells}</div>
    ${g.main && !inGrid && g.mainAt === "bottom" ? `<div class="fl-pmb bottom">MB · Main Breaker</div>` : ""}`;
}

// ---- Menus: open under the tapped control, or above it when there is no room below ----

export function menuHtml(title: string, items: string, left: number, top: number): string {
  return `<div class="fl-shade" data-fl="close"></div><div class="fl-menu" style="left:${left}px;top:${top}px"><div class="fl-mh">${esc(title)}</div>${items}</div>`;
}

/** A lit square: End Section Here, Split, Spare, Clear Section. */
export function squareMenuItems(g: Gear, letter: string, sec: number, space: number): string {
  const p = `${letter}${space}`;
  const word = g.type === "MCC" ? "bucket" : "compartment";
  const ways = g.splits[spaceKey(sec, space)] ?? 1;
  const spare = g.spares.includes(spaceKey(sec, space));
  const splits = [2, 3, 4]
    .map((n) => `<button type="button" data-fl="split" data-n="${n}" class="${ways === n ? "cur" : ""}">Split Into ${["", "", "Two", "Three", "Four"][n]}<small>${SPLIT_LETTERS.slice(0, n).map((x) => p + x).join(", ")}</small></button>`)
    .join("");
  return `<button type="button" data-fl="end">End Section Here<small>${esc(p)} is the last ${word}</small></button>${splits}${
    ways > 1 ? `<button type="button" data-fl="split" data-n="1">Put Back As One<small>${esc(p)}</small></button>` : ""
  }<button type="button" data-fl="spare">${spare ? "Not A Spare" : "Mark Spare"}<small>The label still prints ${esc(p)}</small></button><button type="button" class="red" data-fl="clearsec">Clear Section ${sec + 1}<small>Takes out every ${word} in it</small></button>`;
}

/** A lit circle: 1, 2 or 3 pole down the same side, or Clear. A count that runs into a breaker or off the panel is gray. */
export function circuitMenuItems(g: Gear, top: number): string {
  const cur = g.circuits[String(top)] ?? 1;
  const names = breakerNames(g);
  const name = names.get(top) ?? "";
  return (
    [1, 2, 3]
      .map((p) => {
        const take = polesTakeIn(g, top, p);
        const note = !take ? ", not enough room below" : take.length ? `. Takes in ${take.map((c) => esc(names.get(c) ?? "")).join(", ")}` : "";
        return `<button type="button" data-fl="poles" data-p="${p}" class="${cur === p ? "cur" : ""}"${take ? "" : " disabled"}>${p} Pole<small>Circuit${p > 1 ? "s" : ""} ${circuitsOf(top, p)}${note}</small></button>`;
      })
      .join("") +
    (isGridMain(g, top)
      ? `<button type="button" data-fl="notmain">Not The Main<small>Back to a breaker. The feeders count on from it.</small></button>`
      : `<button type="button" data-fl="gridmain">Main Breaker<small>This breaker is the main. It reads MB.</small></button>`) +
    (cur === 1 && !isGridMain(g, top)
      ? `<button type="button" data-fl="tandem" class="${isTandem(g, top) ? "cur" : ""}">${isTandem(g, top) ? "Not A Tandem" : "Tandem"}<small>${isTandem(g, top) ? `Back to one breaker, ${esc(name)}` : `Two in one space: ${esc(name)}A on top, ${esc(name)}B below`}</small></button>`
      : "") +
    (isGridMain(g, top) ? "" : `<button type="button" data-fl="blank">Blank<small>A filler is installed. No number, no label.</small></button>`) +
    `<button type="button" class="red" data-fl="clearbreaker">Clear ${esc(name)}<small>Back to a space. The breakers after it count down one.</small></button>`
  );
}

/** A Blank (F3a): back to a breaker, or back to a space not walked. */
export function blankMenuItems(): string {
  return `<button type="button" data-fl="unblank" data-b="1">Make It A Breaker<small>A 1 pole here. The breakers after it count up one.</small></button><button type="button" class="red" data-fl="unblank" data-b="0">Clear<small>Back to a space, not walked</small></button>`;
}

/** Covers On Top and Covers At Bottom for one section, 0 to 3 (decision 1). */
export function coverMenuItems(g: Gear, section: number): string {
  const [t, b] = coversOf(g, section);
  const row = (where: "top" | "bottom", cur: number) =>
    Array.from({ length: MAX_COVERS + 1 }, (_, n) => `<button type="button" data-fl="covers" data-w="${where}" data-n="${n}" class="fl-mi-size${n === cur ? " cur" : ""}">${n}</button>`).join("");
  return `<div class="fl-msub">Covers On Top</div><div class="fl-msizes">${row("top", t)}</div><div class="fl-msub">Covers At Bottom</div><div class="fl-msizes">${row("bottom", b)}</div><div class="fl-mnote">A Cover can never take a device. No number, no label. Could it ever take one? Then it is a Spare.</div>`;
}

/** The gear's own label: its size, the size of every bucket or breaker in it, and Remove. */
export function labelMenuItems(g: Gear, own: LabelSize, kids: LabelSize | null): string {
  const pick = (what: "size" | "kidsize", cur: LabelSize) =>
    TORO_MAX_SIZES.map((s) => `<button type="button" data-fl="${what}" data-size="${sizeKey(s)}" class="fl-mi-size${sizeKey(s) === sizeKey(cur) ? " cur" : ""}">${sizeName(s)}</button>`).join("");
  const kidWord = isPanel(g) ? "Breakers" : g.type === "MCC" ? "Buckets" : "Compartments";
  return `<div class="fl-msub">Label Size · ${esc(g.name)}</div><div class="fl-msizes">${pick("size", own)}</div>${
    kids ? `<div class="fl-msub">${kidWord} In It</div><div class="fl-msizes">${pick("kidsize", kids)}</div>` : ""
  }${g.type === "MCC" ? "" : `<button type="button" data-fl="rename" data-g="${esc(g.id)}">Rename<small>Keeps ${esc(g.type)}. Set the number or letter.</small></button>`}<button type="button" data-fl="reprintgear" data-g="${esc(g.id)}">Reprint Labels<small>${esc(g.name)}${kids ? ` and its ${kidWord.toLowerCase()}` : ""}, picked on the print card</small></button><button type="button" class="red" data-fl="rmgear" data-g="${esc(g.id)}">Remove ${esc(g.name)}<small>And its labels</small></button>`;
}

// ---- Sheets ----

export function confirmSheet(title: string, note: string, action: string): string {
  return `<div class="ai-sheet" data-fl-sheet><div class="ai-card">
    <div class="ai-cardhd"><b>${esc(title)}</b></div>
    <div class="ai-annot">${esc(note)}</div>
    <div class="ai-saves two"><button class="ai-sbtn" type="button" data-fl="close">Cancel</button><button class="ai-sbtn red" type="button" data-fl="${esc(action)}">Remove</button></div>
  </div></div>`;
}

export interface RoomsView {
  site: FieldSite;
  cur: string | null;
  kind: RoomKindRev2;
  nums: ReadonlySet<number>;
  part: string;
  /** Changes made since Rooms opened. Cancel puts them back. */
  changes?: number;
  /** A room tapped to change its code: the new kind, number and part picked so far (B3). Null: Add Rooms. */
  recode?: { kind: "ER" | "EA"; num: number; part: string } | null;
}

const KIND_WORDS: Record<RoomKindRev2, [string, string]> = {
  ER: ["ER", "Electrical Room"],
  EA: ["EA", "Electrical Area"],
  OUT: ["Outside", "Yard, roof, pole"],
};

/** Under the room list: Change To EA1 (ER and EA only), then Remove. */
function roomActions(cur: string): string {
  return `<div class="fl-roomacts"><button class="fl-rmroom" type="button" data-fl="rmroom">Remove ${esc(cur)}</button></div>`;
}

/** Change ER6 To: kind, the number grid with Taken numbers, the part, and one button (B3, Part B). */
function recodeSteps(v: RoomsView, from: string, rc: NonNullable<RoomsView["recode"]>): string {
  const to = `${rc.kind}${rc.num}${rc.part}`;
  const problem = recodeProblem(v.site, from, to);
  const have = new Set(v.site.rooms.map((r) => r.name));
  const kinds = (["ER", "EA"] as const)
    .map((k) => `<button type="button" class="${rc.kind === k ? "on" : ""}" data-fl="kind" data-k="${k}">${KIND_WORDS[k][0]}<small>${KIND_WORDS[k][1]}</small></button>`)
    .join("");
  const nums = Array.from({ length: 12 }, (_, i) => i + 1)
    .map((n) => {
      const name = `${rc.kind}${n}${rc.part}`;
      const taken = have.has(name) && name !== from;
      const own = name === from;
      const cls = `fl-num${rc.num === n ? " on" : own ? " own" : ""}${taken ? " used" : ""}`;
      return `<button type="button" class="${cls}" data-fl="num" data-n="${n}"${taken ? " disabled" : ""}>${n}${taken ? "<small>Taken</small>" : own && rc.num !== n ? "<small>Now</small>" : ""}</button>`;
    })
    .join("");
  const parts = ROOM_PARTS.map((p) => `<button type="button" class="${rc.part === p ? "on" : ""}" data-fl="part" data-p="${p}">${p ? `Part ${p}` : "Whole Room"}</button>`).join("");
  const label = problem === null ? `Change To ${esc(to)}` : problem === "" ? "Pick A New Number" : esc(problem);
  return `<div class="fl-hd">Change ${esc(from)} To</div>
    <div class="fl-step"><span>1</span><div class="fl-kinds two">${kinds}</div></div>
    <div class="fl-step"><span>2</span><div><div class="fl-sub">Pick its new number. Grey numbers are taken.</div><div class="fl-nums">${nums}</div></div></div>
    <div class="fl-step"><span>3</span><div><div class="fl-sub">Whole room, or one part of a big space.</div><div class="fl-parts">${parts}</div></div></div>
    <button class="fl-addbtn" type="button" data-fl="recodego"${problem === null ? "" : " disabled"}>${label}</button>
    <div class="fl-sub">Gear, the Done mark and the description go with it. Tap ${esc(from)} again to add rooms.</div>`;
}

/** Rooms: 1 kind, 2 tap every number you have, 3 the part. Rooms stay, with or without gear. */
/** Room Description on its own line under You Are In; nothing when it is empty. Typing updates it in place. */
export function roomWhere(description: string | undefined): string {
  return description ? `${esc(description)}<br>` : "";
}

/** The kind of a room by its name: ER1A is ER, EA3 is EA, Outside is OUT. */
function roomKindOf(name: string): RoomKindRev2 {
  if (name === OUTSIDE) return "OUT";
  return name.startsWith("EA") ? "EA" : "ER";
}

/**
 * On This Site, one block per kind (FIELD_TEST_NICHOLASVILLE_1028.md item 1, Robert 3:19 PM): Electrical Rooms
 * in blue, Electrical Areas in purple, Outside in dark grey. Rooms keep their sorted order inside a block.
 */
function roomBlocks(v: RoomsView): string {
  const heads: Record<RoomKindRev2, string> = { ER: "Electrical Rooms", EA: "Electrical Areas", OUT: "Outside" };
  return (Object.keys(heads) as RoomKindRev2[])
    .map((k) => {
      const rooms = v.site.rooms.filter((r) => roomKindOf(r.name) === k);
      if (!rooms.length) return "";
      const chips = rooms
        .map((r) => `<button class="fl-rchip${r.name === v.cur ? " cur" : ""}" type="button" data-fl="go" data-r="${esc(r.name)}">${light(roomLight(r))}${esc(r.name)}${r.photo ? `<span class="ph-mark in" role="img" aria-label="Has a room photo">${CAMERA}</span>` : ""}<small>${r.description ? `${esc(r.description)} · ` : ""}${r.gear.length} gear${r.done ? " · Done" : ""}</small></button>`)
        .join("");
      return `<div class="fl-rblock k-${k}"><div class="fl-rbhd">${heads[k]}</div><div class="fl-rlist">${chips}</div></div>`;
    })
    .join("");
}

export function roomsSheet(v: RoomsView): string {
  const out = v.kind === "OUT";
  const have = new Set(v.site.rooms.map((r) => r.name));
  const ready = roomNames(v.kind, [...v.nums], v.part).filter((n) => !have.has(n));
  const list = v.site.rooms.length
    ? `<div class="fl-hd">On This Site · Pick One, Then Done</div>${roomBlocks(v)}${v.cur ? roomActions(v.cur) : ""}`
    : "";
  const kinds = (Object.keys(KIND_WORDS) as RoomKindRev2[])
    .map((k) => `<button type="button" class="${v.kind === k ? "on" : ""}" data-fl="kind" data-k="${k}">${KIND_WORDS[k][0]}<small>${KIND_WORDS[k][1]}</small></button>`)
    .join("");
  const nums = Array.from({ length: 12 }, (_, i) => i + 1)
    .map((n) => {
      const used = have.has(`${v.kind}${n}${v.part}`);
      return `<button type="button" class="fl-num${v.nums.has(n) ? " on" : ""}${used ? " used" : ""}" data-fl="num" data-n="${n}"${used ? " disabled" : ""}>${n}${used ? "<small>Added</small>" : ""}</button>`;
    })
    .join("");
  const parts = ROOM_PARTS.map((p) => `<button type="button" class="${v.part === p ? "on" : ""}" data-fl="part" data-p="${p}">${p ? `Part ${p}` : "Whole Room"}</button>`).join("");
  const steps = out
    ? `<div class="ai-annot">Outside is one place: the utility, the yard, the roof. No number. Its IDs read UTL.</div>`
    : `<div class="fl-step"><span>2</span><div><div class="fl-sub">Tap every number you have. Tap 1 to 5 for five rooms.</div><div class="fl-nums">${nums}</div></div></div>
       <div class="fl-step"><span>3</span><div><div class="fl-sub">One big space with two rooms? Pick its part.</div><div class="fl-parts">${parts}</div></div></div>`;
  return `<div class="ai-sheet" data-fl-sheet><div class="ai-card fl-wide">
    <div class="ai-cardhd"><b>Rooms</b><span class="fl-hdbtns"><button class="ai-tbtn" type="button" data-fl="roomscancel">Cancel</button><button class="ai-tbtn dark" type="button" data-fl="roomsdone">Done</button></span></div>
    ${v.changes ? `<div class="fl-chgline">${plural(v.changes, "change")} since you opened Rooms. Cancel puts ${v.changes === 1 ? "it" : "them"} back. Done keeps ${v.changes === 1 ? "it" : "them"}.</div>` : ""}
    ${list}
    ${
      v.recode && v.cur && v.cur !== OUTSIDE
        ? recodeSteps(v, v.cur, v.recode)
        : `<div class="fl-hd">Add Rooms</div>
    <div class="fl-step"><span>1</span><div class="fl-kinds">${kinds}</div></div>
    ${steps}
    <button class="fl-addbtn" type="button" data-fl="addrooms"${ready.length ? "" : " disabled"}>${ready.length ? `Add ${esc(ready.join(", "))}` : out ? "Outside Is Added" : "Pick A Number"}</button>`
    }
  </div></div>`;
}

/** Add A Panel: a new panel, or a subpanel of the panel just before (decision 2). */
export function panelPickSheet(next: string, sub: string, parent: string): string {
  return `<div class="ai-sheet" data-fl-sheet><div class="ai-card">
    <div class="ai-cardhd"><b>Add A Panel</b><button class="ai-tbtn" type="button" data-fl="close">Cancel</button></div>
    <div class="ai-saves two"><button class="ai-sbtn" type="button" data-fl="addplain">${esc(next)}<small>New Panel</small></button><button class="ai-sbtn dark" type="button" data-fl="addsub">${esc(sub)}<small>Subpanel Of ${esc(parent)}</small></button></div>
  </div></div>`;
}

/** Rename PNL-03: the code is fixed, one box for the number or letter part. */
export function renameSheet(name: string, type: string, value: string, problem: string | null): string {
  const ok = problem === null;
  return `<div class="ai-sheet" data-fl-sheet><div class="ai-card">
    <div class="ai-cardhd"><b>Rename ${esc(name)}</b><button class="ai-tbtn" type="button" data-fl="close">Cancel</button></div>
    <div class="ai-annot">The code stays ${esc(type)}, so the Asset ID still builds. Set the number or letter part: 2A, 04. Labels already printed read Changed.</div>
    <label class="fl-renrow"><b>${esc(type)}-</b><input type="text" data-fl-in="rename" value="${esc(value)}" maxlength="3" autocomplete="off" autocapitalize="characters" spellcheck="false" inputmode="text"></label>
    <div class="fl-renline" id="fl-renline">${problem ? `<div class="ac-line red">${esc(problem)}</div>` : ""}</div>
    <button class="fl-addbtn" type="button" data-fl="renamego" id="fl-renamego"${ok ? "" : " disabled"}>Rename</button>
  </div></div>`;
}

/** Every equipment type, for More. */
export function typesSheet(types: readonly { code: string; name: string }[]): string {
  return `<div class="ai-sheet" data-fl-sheet><div class="ai-card">
    <div class="ai-cardhd"><b>More Equipment Types</b><button class="ai-tbtn" type="button" data-fl="close">Cancel</button></div>
    <div class="fl-typelist">${types.map((t) => `<button type="button" data-fl="add" data-type="${esc(t.code)}"><b>${esc(t.code)}</b><span>${esc(t.name)}</span></button>`).join("")}</div>
  </div></div>`;
}

export function sizesCard(): string {
  const row = (what: string, s: LabelSize) => `<tr><td>${what}</td><td><b>${sizeName(s)}</b></td></tr>`;
  return `<div class="ai-sheet" data-fl-sheet><div class="ai-card">
    <div class="ai-cardhd"><b>Label Sizes · Toro Max</b><button class="ai-tbtn" type="button" data-fl="close">Close</button></div>
    <div class="ai-annot">Defaults by what is labeled. Tap the gear's label on its card to change its size, or the size of every bucket or breaker in it. Text holds its height for the material and may shrink up to 10% to stay on a size.</div>
    <table class="fl-def">
      ${row("Panel breaker, inside a panel", DEFAULT_SIZES.breaker)}
      ${row("MCC bucket, switchboard or switchgear compartment", DEFAULT_SIZES.bucket)}
      ${row("Panel, enclosure, VFD, disconnect, transformer, junction box, every other type", DEFAULT_SIZES.housing)}
      ${row("MCC, switchboard, switchgear", DEFAULT_SIZES.big)}
    </table>
    <div class="ai-annot">Paper sizes the driver needs: ${TORO_MAX_SIZES.map(sizeName).join(", ")}.</div>
  </div></div>`;
}

// ---- Print preview ----
// The preview is the one print card now (print-card.ts, field-labels-page.ts fieldCardView). ONE_PRINT_WORDS.md.
